import { Flex, Heading, Square } from '@grubhubprod/gh-cookbook-react';
import React, { useEffect, useRef, useState } from 'react';
import { Container, Stack } from '@grubhubprod/gh-cookbook-react/dist';
import { addDays, endOfDay, startOfDay, subDays } from 'date-fns';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import {
  IFilterParams,
  ISearchParams,
  SearchStatus,
  useDepartmentSearch,
  useLocationSearch,
  useOrderSearch,
} from 'src/utilities/useSearchHooks';
import Table from 'src/components/Table/Table';
import { Pagination } from 'src/components/Pagination/Pagination';
import TableRow from 'src/components/enterprise/features/OrderHistory/OrderList/TableRow';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import TableHeader, {
  defaultOrderHistorySortConfig,
} from 'src/components/enterprise/features/OrderHistory/OrderList/TableHeader';
import { SortFields, SortOrder } from 'src/components/Table/SortableColumn';
import { AvailableOrderTypes } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { getConfig } from 'src/utilities/config';
import { EnterpriseEmptyState } from 'src/components/enterprise/EnterpriseEmptyState/EnterpriseEmptyState';
import { Filters } from 'src/components/enterprise/features/OrderHistory/OrderList/Filters';
import { getHomeBreadcrumb } from 'src/constants/urls';

export const ORDERS_PAGE_SIZE = 10;

interface SearchFormParams {
  query?: string;
  page?: number;
  sortField?: SortFields;
  sortOrder?: SortOrder;
}

export interface OrderHistoryFilters {
  end?: string;
  start?: string;
  order_types?: string[];
  departments?: string[];
  locations?: string[];
}

export interface OrderHistorySearchConfig {
  searchParams: SearchFormParams;
  filters: OrderHistoryFilters;
}

export default function OrderHistory(): JSX.Element {
  const { preOrderExtension } = getConfig();
  const { corpData, corpId } = useAuth();
  const isCorpExpenseEnabled = corpData.expenseCodesEnabled;
  const availableOrderTypes = getAvailableOrderTypes();
  const defaultOrderHistoryConfig = {
    searchParams: {
      page: 1,
      sortField: defaultOrderHistorySortConfig.sortField,
      sortOrder: defaultOrderHistorySortConfig.sortOrder,
    },
    filters: {
      start: subDays(startOfDay(new Date()), 14).toISOString(),
      end: addDays(endOfDay(new Date()), preOrderExtension).toISOString(),
      order_types: availableOrderTypes,
    },
  };
  const filtersRef = useRef(null);
  const [searchConfig, setSearchConfig] = useState<OrderHistorySearchConfig>(
    defaultOrderHistoryConfig
  );
  const hasFilterApplied = useRef(false);

  const {
    doSearch: doOrderSearch,
    resultsList: orders,
    currentSearchPage,
    totalPages,
    totalResults,
    hasNoResultsWithQuery,
    searchStatus,
  } = useOrderSearch(corpId, ORDERS_PAGE_SIZE);

  const { doSearch: doDepartmentSearch, totalResults: numberOfDepartments } =
    useDepartmentSearch(corpId, true);

  const { doSearch: doLocationSearch, totalResults: numberOfLocations } =
    useLocationSearch(corpId);

  const hasNoResultsWithQueryOrFilterApplied =
    hasFilterApplied.current || hasNoResultsWithQuery;

  useEffect(() => {
    const { searchParams, filters } = searchConfig;
    if (corpId) {
      searchOrders(searchParams, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId, searchConfig]);

  useEffect(() => {
    if (corpId) {
      doDepartmentSearch();
      doLocationSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  async function searchOrders(
    { query, page, sortOrder, sortField }: SearchFormParams,
    { start, end, order_types, locations, departments }: OrderHistoryFilters
  ) {
    const searchParams: ISearchParams = {};
    const filters: IFilterParams = {};

    if (query) {
      searchParams.query = query;
    }

    if (page) {
      searchParams.page = page;
    }

    if (sortOrder && sortField) {
      searchParams.sort_field = sortField;
      searchParams.sort_order = sortOrder;
    }

    filters.start = start;
    filters.end = end;

    if (
      Array.isArray(order_types) &&
      order_types.length > 0 &&
      order_types.length < availableOrderTypes.length
    ) {
      filters.order_types = order_types;
    }

    if (Array.isArray(locations) && locations.length > 0) {
      filters.locations = locations;
    }

    if (Array.isArray(departments) && departments.length > 0) {
      filters.departments = departments;
    }

    await doOrderSearch(searchParams, false, filters);
  }

  function onSearchParamsChange(newSearchParams: SearchFormParams) {
    const { searchParams, filters } = searchConfig;
    setSearchConfig({
      searchParams: { ...searchParams, ...newSearchParams },
      filters,
    });
  }

  function onFilterChange({
    filters: newFilters,
    searchParams: { query },
  }: {
    searchParams: Partial<SearchFormParams>;
    filters: Partial<OrderHistoryFilters>;
  }) {
    const { searchParams, filters: filtersConfig } = searchConfig;
    const filters = { ...filtersConfig, ...newFilters };

    setSearchConfig({
      searchParams: { ...searchParams, page: 1, query },
      filters,
    });
    hasFilterApplied.current = true;
  }

  function getAvailableOrderTypes() {
    return Object.keys(AvailableOrderTypes).filter((orderTypeKey) => {
      // we should only show the GROUP order type if Group Orders
      // are enabled as a feature on the corp
      if (AvailableOrderTypes[orderTypeKey] === AvailableOrderTypes.GROUP) {
        return corpData.groupOrderEnabled;
      }
      return true;
    });
  }

  const shouldDisplayDepartments = numberOfDepartments > 1;
  const shouldDisplayLocations = numberOfLocations > 1;

  function reset() {
    setSearchConfig(defaultOrderHistoryConfig);
    if (filtersRef.current) {
      filtersRef.current.reset();
    }
    hasFilterApplied.current = false;
  }

  const renderOrderHistoryRows = () => {
    return (
      <ul className="removeListStyles">
        {orders.map((order) => (
          <TableRow
            key={order.order_id}
            order={order}
            corpId={corpId}
            isCorpExpenseEnabled={isCorpExpenseEnabled}
            shouldDisplayDepartments={shouldDisplayDepartments}
            shouldDisplayLocations={shouldDisplayLocations}
          />
        ))}
      </ul>
    );
  };

  return (
    <>
      <Stack backgroundColor="untintedBackground">
        <Container variant="default">
          <Square inset="lg">
            <PageHeader>
              <Heading variant="h1">Orders</Heading>
              <Breadcrumbs
                breadcrumbs={[
                  {
                    label: 'Admin home',
                    ...getHomeBreadcrumb(corpId),
                  },
                ]}
              />
            </PageHeader>
            <Filters
              ref={filtersRef}
              corpId={corpId}
              filters={searchConfig.filters}
              onFilterChange={onFilterChange}
              availableOrderTypes={availableOrderTypes}
              onSearchParamsChange={({ query }) => {
                onSearchParamsChange({ query, page: 1 });
              }}
              shouldDisplayDepartments={shouldDisplayDepartments}
              shouldDisplayLocations={shouldDisplayLocations}
            />
          </Square>
        </Container>
      </Stack>

      <Container>
        <Square inset="lg">
          <Stack y="md" backgroundColor="untintedBackground">
            <Table
              isLoading={searchStatus === SearchStatus.LOADING}
              header={() => (
                <TableHeader
                  isCorpExpenseEnabled={isCorpExpenseEnabled}
                  onSortColumn={(
                    sortField: SortFields,
                    sortOrder: SortOrder
                  ) => {
                    onSearchParamsChange({
                      sortField,
                      sortOrder,
                      page: 1,
                    });
                  }}
                  shouldDisplayDepartments={shouldDisplayDepartments}
                  shouldDisplayLocations={shouldDisplayLocations}
                />
              )}
              body={() =>
                totalResults > 0 ? (
                  renderOrderHistoryRows()
                ) : (
                  <EnterpriseEmptyState
                    heading="No results found."
                    subheading={
                      hasNoResultsWithQueryOrFilterApplied &&
                      'Adjust your search term or filters above or click the button to start over.'
                    }
                    imageUrl="https://res.cloudinary.com/grubhub-assets/image/upload/v1605271831/corps-admin-ui/illustration_utensils.svg"
                    buttonName="Reset all"
                    onButtonClick={reset}
                    showButton={hasNoResultsWithQueryOrFilterApplied}
                  />
                )
              }
              showFooter={false}
            />
          </Stack>
          {totalPages > 1 && (
            <Flex justifyContent="center">
              <Pagination
                boundaryLinks
                page={currentSearchPage}
                maxSize={8}
                pageSize={ORDERS_PAGE_SIZE}
                ellipses={false}
                rotate
                collectionSize={totalResults}
                pageChange={(page) => {
                  onSearchParamsChange({
                    page,
                  });
                }}
              />
            </Flex>
          )}
        </Square>
      </Container>
    </>
  );
}
